import React, { forwardRef, useCallback, useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { css, keyframes, useTheme } from '@emotion/react';
import PropTypes from 'prop-types';

import { AlertTypes } from '../../constants';
import { useMountedRef } from '../../utils/hooks';
import { Icon, IconTypes } from '../icons';
import { Icon as LumenIcon } from '../../lumen/components';
import { theme as lumenTheme } from '../../lumen/theme';

const fadeOut = keyframes`
  from { opacity: 1 }
  to { opacity: 0 }
`;

const Alert = forwardRef(function Alert(
  {
    type,
    className,
    children,
    disappearing = false,
    cancellable = false,
    showing: showingProp,
    onHide = () => {},
    duration = 3,
    dismissTrackingItem,
    ...additionalProps
  },
  ref
) {
  const contextTheme = useTheme();
  const theme = contextTheme?.color ? contextTheme : lumenTheme;
  const [showingState, setShowingState] = useState(true);
  const showing = showingProp ?? showingState;
  if (!showing) {
    return null;
  }
  function hide() {
    setShowingState(false);
    onHide();
  }
  const tone = (() => {
    switch (type) {
      case AlertTypes.SUCCESS:
        return {
          icon: IconTypes.CIRCULAR_CHECK,
          alt: 'check',
          bg: theme.color.green[50],
          border: theme.color.green[200],
          fg: theme.color.green[700]
        };
      case AlertTypes.ERROR:
        return {
          icon: IconTypes.CIRCULAR_CLOSE,
          alt: 'error',
          bg: theme.color.red[50],
          border: theme.color.red[200],
          fg: theme.color.red[700]
        };
      case AlertTypes.WARNING:
        return {
          icon: IconTypes.CIRCULAR_EXCLAMATION,
          alt: 'warning',
          bg: theme.color.amber[50],
          border: theme.color.amber[200],
          fg: theme.color.amber[700]
        };
      case AlertTypes.INFO:
        return {
          icon: IconTypes.CIRCULAR_INFO,
          alt: 'info',
          bg: theme.color.blue[50],
          border: theme.color.blue[200],
          fg: theme.color.blue[800]
        };
      default:
        return {};
    }
  })();

  return (
    <div
      {...additionalProps}
      ref={ref}
      className={cx('alert', className)}
      css={[
        css`
          display: inline-block;
          box-sizing: border-box;
          border-radius: ${theme.radius.base};
          border: 1px solid ${tone.border};
          background: ${tone.bg};
          color: ${tone.fg};
          font-family: ${theme.font.sans};
          font-size: ${theme.fontSize.base};
        `,
        disappearing &&
          css`
            animation: ${fadeOut} 0.2s ease-in-out ${duration}s forwards;
          `
      ]}
      onAnimationEnd={disappearing ? hide : undefined}
      data-test-id="alert"
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 14px;
        `}
      >
        {tone.icon && (
          <Icon
            type={tone.icon}
            alt={tone.alt}
            css={css`
              flex-shrink: 0;
              margin-top: 2px;
            `}
          />
        )}
        <div
          css={css`
            min-width: 0;
            flex: 1;
            align-self: center;
          `}
        >
          {children}
        </div>
        {cancellable && (
          <button
            css={css`
              margin-left: auto;
              flex-shrink: 0;
              align-self: flex-start;
              width: 20px;
              height: 20px;
              margin-top: 2px;
              padding: 0;
              display: grid;
              place-items: center;
              border: 0;
              background: none;
              cursor: pointer;
              color: ${theme.color.fg.subtle};
              border-radius: ${theme.radius.sm};

              &:hover {
                background: rgba(0, 0, 0, 0.05);
                color: ${theme.color.fg.default};
              }
            `}
            aria-label="Dismiss"
            onClick={hide}
            data-tracking-item={dismissTrackingItem}
          >
            <LumenIcon name="xmark" size={14} />
          </button>
        )}
      </div>
    </div>
  );
});

Alert.propTypes = {
  type: PropTypes.oneOf(_.values(AlertTypes)).isRequired,
  disappearing: PropTypes.bool,
  cancellable: PropTypes.bool,
  onHide: PropTypes.func,
  showing: PropTypes.bool,
  duration: PropTypes.number,
  dismissTrackingItem: PropTypes.string
};

export default Alert;

/**
 * Hook for managing the state of an OverlayAlert.
 *
 * @returns {Object} - An object with fields:
 *                     status - The alert status
 *                     setStatus - Function to set the status
 */
export function useOverlayAlertController() {
  const [status, setStatus] = useState('none');
  const [message, setMessage] = useState('');
  const mounted = useMountedRef();

  const showWarning = useCallback(
    warningMessage => {
      if (mounted.current) {
        setStatus('failure');
        setMessage(warningMessage);
      }
    },
    [mounted]
  );

  const showSuccess = useCallback(
    successMessage => {
      if (mounted.current) {
        setStatus('success');
        setMessage(successMessage);
      }
    },
    [mounted]
  );

  const clearAlert = useCallback(() => {
    if (mounted.current) {
      setStatus('none');
      setMessage('');
    }
  }, [mounted]);

  return { status, message, showWarning, showSuccess, clearAlert };
}

/**
 * Component for showing an short-term alert message that replaces another
 * component.
 *
 * If status is none, this just shows whatever its children are.  If status is
 * success or failure, it replaces the children with a success or failure
 * banner.  See the "Copy link" button in the "Export" side panel tab for
 * a straightforward example.
 */
export function OverlayAlert({
  controller,
  className,
  children,
  onHideSuccess,
  cancellable = false,
  duration = 1,
  failureDuration = 2
}) {
  if (controller.status === 'none') {
    return children;
  }

  return (
    <Alert
      type={
        controller.status === 'success'
          ? AlertTypes.SUCCESS
          : AlertTypes.WARNING
      }
      className={className}
      disappearing
      onHide={() => {
        if (controller.status === 'success') {
          onHideSuccess?.();
        }
        controller.clearAlert();
      }}
      cancellable={cancellable}
      duration={controller.status === 'success' ? duration : failureDuration}
    >
      {controller.message}
    </Alert>
  );
}
